import {
  ADD_NEW_STORE_PATH,
  DELIVERY_MANAGEMENT_DELIVERIES_PATH,
  DELIVERY_MANAGEMENT_DRIVERS_PATH,
  DELIVERY_PERFORMANCE_REPORTING_PATH,
  EZ_DISPATCH_PREFERENCES_PATH,
  MARKETING_ACTIVITY_PATH,
  ORDER_CANCELLED_PATH,
  ORDER_COMPLETED_PATH,
  ORDER_INDEX_PATH,
  ORDER_REJECTED_PATH,
  PROMOTIONS_PATH,
  RANKINGS_PATH,
  SETTINGS_ACCOUNT_PATH,
  SETTINGS_PERSONAL_STORE_GROUPS_PATH,
  SETTINGS_SERVICES_PATH,
  SETTINGS_STORE_PATH,
  SETTINGS_USERS_PATH,
  SPONSORED_LISTING_CAMPAIGNS_PATH,
  PROMOS_PATH,
} from '@src/paths';
import {type TFunction} from 'i18next';
import {
  NavQuery_me_catererAccount,
  NavQuery_ezManagePermissions,
} from '@src/__generated__/NavQuery';
import {SubNavItem} from '../types';

export const getActiveLink = (path: string, links: string[]) => {
  // Split the current path into segments
  const pathSegments = path.split('/').filter(Boolean);

  let bestMatch: string | null = null;
  let bestMatchSegments = 0;

  links.forEach(link => {
    // Split each link into segments
    const linkSegments = link.split('/').filter(Boolean);

    // Check if this link is a potential match
    const isMatch = linkSegments.every((segment, index) => pathSegments[index] === segment);

    // If it's a match and has more matching segments than our current best match
    if (isMatch && linkSegments.length > bestMatchSegments) {
      bestMatch = link;
      bestMatchSegments = linkSegments.length;
    }

    // If it's an exact match, use it immediately
    if (link === path) {
      bestMatch = link;
    }
  });

  return bestMatch;
};

export const checkIsActive = (link: string, path: string) => {
  return path.includes(link);
};

const addCommonPropsToSubnavItems = ({
  items,
  isLinkToActiveApp,
  activeLink,
}: {
  items: {label: string; to: string; isLinkToActiveApp?: boolean}[];
  isLinkToActiveApp: boolean;
  activeLink: string;
}): Omit<SubNavItem, 'section'>[] => {
  const activeNavLink = getActiveLink(
    activeLink,
    items.map(item => item.to)
  );

  return items.map(item => ({
    ...item,
    isLinkToActiveApp:
      item.isLinkToActiveApp === undefined ? isLinkToActiveApp : item.isLinkToActiveApp,
    isActive: item.to === activeNavLink,
  }));
};

export const buildOrdersSubnavItems = ({
  isLinkToActiveApp,
  t,
  absoluteRoot,
  activeLink,
}: {
  isLinkToActiveApp: boolean;
  t: TFunction<'translation', undefined>;
  absoluteRoot: string;
  activeLink: string;
}): Omit<SubNavItem, 'section'>[] => {
  const ordersSubnavItems: {label: string; to: string}[] = [
    {
      label: t('pages.OrderIndex.tabs.upcoming'),
      to: isLinkToActiveApp ? ORDER_INDEX_PATH : `${absoluteRoot}${ORDER_INDEX_PATH}`,
    },
    {
      label: t('pages.OrderIndex.tabs.completed'),
      to: isLinkToActiveApp ? ORDER_COMPLETED_PATH : `${absoluteRoot}${ORDER_COMPLETED_PATH}`,
    },
    {
      label: t('pages.OrderIndex.tabs.rejected'),
      to: isLinkToActiveApp ? ORDER_REJECTED_PATH : `${absoluteRoot}${ORDER_REJECTED_PATH}`,
    },
    {
      label: t('pages.OrderIndex.tabs.canceled'),
      to: isLinkToActiveApp ? ORDER_CANCELLED_PATH : `${absoluteRoot}${ORDER_CANCELLED_PATH}`,
    },
  ];
  return addCommonPropsToSubnavItems({items: ordersSubnavItems, isLinkToActiveApp, activeLink});
};

export const buildDeliverySubNavItems = ({
  t,
  isLinkToActiveApp,
  absoluteRoot,
  currentAppRoot = '',
  activeLink,
}: {
  t: TFunction<'translation', undefined>;
  isLinkToActiveApp: boolean;
  absoluteRoot: string;
  currentAppRoot: string | undefined;
  activeLink: string;
}): Omit<SubNavItem, 'section'>[] => {
  const items = [
    {
      label: t('components.Navigation.Delivery.subnav.deliveries'),
      to: isLinkToActiveApp
        ? DELIVERY_MANAGEMENT_DELIVERIES_PATH
        : `${absoluteRoot}${DELIVERY_MANAGEMENT_DELIVERIES_PATH}`,
    },
    {
      label: t('components.Navigation.Delivery.subnav.drivers'),
      to: isLinkToActiveApp
        ? DELIVERY_MANAGEMENT_DRIVERS_PATH
        : `${absoluteRoot}${DELIVERY_MANAGEMENT_DRIVERS_PATH}`,
    },
  ];

  // ezdispatch and delivery performance are housed in ezmanage,
  // but other subnav links are in delivery, hence the hacky workaround
  const additionalEzManageDeliveryLinks: Omit<SubNavItem, 'section' | 'isActive'>[] = [
    {
      label: t('components.Navigation.Delivery.subnav.ezDispatch'),
      isLinkToActiveApp: !isLinkToActiveApp,
      to: isLinkToActiveApp
        ? `${currentAppRoot}${EZ_DISPATCH_PREFERENCES_PATH}`
        : EZ_DISPATCH_PREFERENCES_PATH,
    },
    {
      label: t('components.Navigation.Delivery.subnav.deliveryPerformanceReporting'),
      isLinkToActiveApp: !isLinkToActiveApp,
      to: isLinkToActiveApp
        ? `${currentAppRoot}${DELIVERY_PERFORMANCE_REPORTING_PATH}`
        : DELIVERY_PERFORMANCE_REPORTING_PATH,
    },
  ];

  return addCommonPropsToSubnavItems({
    items: [...items, ...additionalEzManageDeliveryLinks],
    isLinkToActiveApp,
    activeLink,
  });
};

export const buildMarketingToolsSubnavItems = ({
  t,
  isLinkToActiveApp,
  absoluteRoot,
  activeLink,
  catererAccount,
  ezManagePermissions,
  showSelfServePromoTool,
}: {
  t: TFunction<'translation', undefined>;
  isLinkToActiveApp: boolean;
  absoluteRoot: string;
  catererAccount: NavQuery_me_catererAccount | null;
  ezManagePermissions: NavQuery_ezManagePermissions | null;
  activeLink: string;
  showSelfServePromoTool: boolean;
}): Omit<SubNavItem, 'section'>[] => {
  const items = [
    catererAccount?.canViewSponsoredListings && {
      label: t('components.Navigation.Marketing.subnav.sponsoredListings'),
      to: isLinkToActiveApp
        ? SPONSORED_LISTING_CAMPAIGNS_PATH
        : `${absoluteRoot}${SPONSORED_LISTING_CAMPAIGNS_PATH}`,
    },
    ezManagePermissions?.canManagePromos &&
      showSelfServePromoTool && {
        label: t('components.Navigation.Marketing.subnav.sponsoredPromos'),
        to: isLinkToActiveApp ? PROMOS_PATH : `${absoluteRoot}${PROMOS_PATH}`,
      },
    catererAccount?.canViewSponsoredListings && {
      label: t('components.Navigation.Marketing.subnav.marketingActivity'),
      to: isLinkToActiveApp ? MARKETING_ACTIVITY_PATH : `${absoluteRoot}${MARKETING_ACTIVITY_PATH}`,
    },
    ezManagePermissions?.canViewRankingsIndexPage && {
      label: t('components.Navigation.Marketing.subnav.ezrewardsAndPPP'),
      to: isLinkToActiveApp ? RANKINGS_PATH : `${absoluteRoot}${RANKINGS_PATH}`,
    },
    ezManagePermissions?.canManagePromos && {
      label: t('components.Navigation.Marketing.subnav.promotions'),
      to: isLinkToActiveApp ? PROMOTIONS_PATH : `${absoluteRoot}${PROMOTIONS_PATH}`,
    },
  ].filter(item => !!item) as {label: string; to: string}[];

  let resolvedActiveLink = activeLink;
  // Special case: map /marketing-tools/sponsored-listings to /marketing-tools/sponsored-listing-campaigns
  // for active link highlighting
  if (activeLink === '/marketing-tools/sponsored-listings') {
    resolvedActiveLink = SPONSORED_LISTING_CAMPAIGNS_PATH;
  }

  return addCommonPropsToSubnavItems({
    items,
    isLinkToActiveApp,
    activeLink: resolvedActiveLink,
  });
};

export const buildSettingsSubnavItems = ({
  t,
  isLinkToActiveApp,
  absoluteRoot,
  settingsPermissions,
  activeLink,
}: {
  accountId?: string;
  t: TFunction<'translation', undefined>;
  isLinkToActiveApp: boolean;
  absoluteRoot: string;
  settingsPermissions: {
    canEditUserSettings: boolean;
    canViewStoreSettingsPage: boolean;
    canAccessPersonalStoreGroups: boolean;
  };
  activeLink: string;
}): Omit<SubNavItem, 'section'>[] => {
  const accountSubNavItems = [
    settingsPermissions.canViewStoreSettingsPage && {
      label: t('components.Navigation.Settings.subnav.storeSettings'),
      to: isLinkToActiveApp ? SETTINGS_STORE_PATH : `${absoluteRoot}${SETTINGS_STORE_PATH}`,
    },
    settingsPermissions.canAccessPersonalStoreGroups && {
      label: t('components.Navigation.Settings.subnav.groups'),
      to: isLinkToActiveApp
        ? SETTINGS_PERSONAL_STORE_GROUPS_PATH
        : `${absoluteRoot}${SETTINGS_PERSONAL_STORE_GROUPS_PATH}`,
    },
    {
      label: t('components.Navigation.Settings.subnav.servicesAndFees'),
      to: isLinkToActiveApp ? SETTINGS_SERVICES_PATH : `${absoluteRoot}${SETTINGS_SERVICES_PATH}`,
    },
    settingsPermissions.canEditUserSettings && {
      label: t('components.Navigation.Settings.subnav.usersAndPermissions'),
      to: isLinkToActiveApp ? SETTINGS_USERS_PATH : `${absoluteRoot}${SETTINGS_USERS_PATH}`,
    },
    {
      label: t('components.Navigation.Settings.subnav.account'),
      to: isLinkToActiveApp ? SETTINGS_ACCOUNT_PATH : `${absoluteRoot}${SETTINGS_ACCOUNT_PATH}`,
    },
    {
      label: t('components.Navigation.Settings.subnav.addNewStore'),
      to: isLinkToActiveApp ? ADD_NEW_STORE_PATH : `${absoluteRoot}${ADD_NEW_STORE_PATH}`,
    },
  ].filter(item => !!item) as {label: string; to: string}[];
  return addCommonPropsToSubnavItems({items: accountSubNavItems, isLinkToActiveApp, activeLink});
};
